import React, { useState, useCallback, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import { Input } from 'ncoded-component-library';
import useMapboxGeocoding from 'hooks/useMapboxGeocoding';
import useListKeyboardNavigation from 'hooks/useListKeyboardNavigation';
import { GeocodingOptions, MapboxFeature } from 'services/mapboxGeocoding';
import SearchIcon from 'icons/Search.icon';
import { DEBOUNCE_TIME } from 'data/global';
import { useTranslation } from 'react-i18next';
import { formatAddressText } from './utils';

import './AddressSearch.styles.scss';

const INITIAL_OPTIONS = { country: ['AU', 'NZ'], limit: 5 };

export type AddressSearchProps = {
  className?: string;
  value?: string;
  placeholder?: string;
  debounceMs?: number;
  searchOptions?: GeocodingOptions;
  onChangeValue?: (value: string) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement, Element>) => void;
};

const AddressSearch: React.FC<AddressSearchProps> = (props) => {
  const { t } = useTranslation();

  const {
    className,
    value,
    placeholder = t('searchForAddress'),
    debounceMs = DEBOUNCE_TIME,
    searchOptions = INITIAL_OPTIONS,
    onBlur,
    onChangeValue,
  } = props;

  const [searchValue, setSearchValue] = useState(value || '');
  const [showResults, setShowResults] = useState(false);

  const { results, isLoading, searchAddresses, clearResults } =
    useMapboxGeocoding();

  const debouncedSearch = useMemo(() => {
    let timeoutId: NodeJS.Timeout;

    return (query: string) => {
      clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        if (query.trim()) {
          searchAddresses(query, searchOptions);
          setShowResults(true);
        } else {
          clearResults();
          setShowResults(false);
        }
      }, debounceMs);
    };
  }, [searchAddresses, clearResults, searchOptions, debounceMs]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);
      onChangeValue?.(value);
      debouncedSearch(value);
    },
    [debouncedSearch, onChangeValue],
  );

  const handleAddressSelect = useCallback(
    (address: MapboxFeature) => {
      const addressText = address.place_name;

      setShowResults(false);
      setSearchValue(addressText);
      onChangeValue?.(addressText);
    },
    [onChangeValue],
  );

  const { activeIndex, onMouseEnter, onKeyDown } = useListKeyboardNavigation({
    showResults,
    itemsCount: results.length,
    onSelect: (index) => handleAddressSelect(results[index]),
  });

  const handleInputFocus = useCallback(() => {
    if (results.length > 0) {
      setShowResults(true);
    }
  }, [results.length]);

  const handleInputBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement, Element>) => {
      setTimeout(() => {
        setShowResults(false);
        onBlur?.(e);
      }, DEBOUNCE_TIME);
    },
    [onBlur],
  );

  useEffect(() => {
    if (value) setSearchValue(value);
  }, [value]);

  return (
    <div className={classNames('address-search', className)}>
      <Input
        value={searchValue}
        placeholder={placeholder}
        prefixNode={<SearchIcon />}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDownCapture={onKeyDown}
      />

      {showResults &&
        (results.length > 0 ? (
          <div className="address-search__results">
            {results.map((result, index) => (
              <div
                key={result.id}
                className={classNames({
                  'address-search__results__item--active':
                    activeIndex === index,
                })}
                onClick={() => handleAddressSelect(result)}
                onMouseEnter={() => onMouseEnter(index)}
              >
                <div className="address-search__results__title">
                  {formatAddressText(result)}
                </div>
                <div className="address-search__results__subtitle">
                  {result.text}
                </div>
              </div>
            ))}
          </div>
        ) : (
          searchValue &&
          !isLoading && (
            <div
              className={classNames(
                'address-search__results',
                'address-search__no-results',
              )}
            >
              {t('noAddressesFound', { searchValue })}
            </div>
          )
        ))}
    </div>
  );
};

export default AddressSearch;
